import {
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Input,
  Link,
} from '@chakra-ui/react';
import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { TabItem } from '../hooks/useTabOrder';
import { useJustDraggedRef } from './DraggableTabLinkContext';
import { TAB_HIT_PADDING_X } from './tab-indicator';

type Variant = 'bar' | 'menu';

type DraggableTabLinkProps = {
  tab: TabItem;
  variant: Variant;
  isActive: boolean;
  onNavigate?: () => void;
  testId?: string;
  /** Bar tabs report their DOM element so the active indicator can slide. */
  tabRef?: (el: HTMLAnchorElement | null) => void;
  /** Optional trailing content (e.g. 3-dot menu). */
  trailing?: ReactNode;
  children?: ReactNode;
  /** Menu variant only: render an inline rename input instead of the link. */
  editing?: boolean;
  editableValue?: string;
  onRename?: (val: string) => void;
};

const BAR_TEXT_COLOR = '#8F9CB2';
const ACTIVE_TEXT_COLOR = 'text.primary';
const MENU_ACTIVE_BG = 'var(--ui-green-350)';
const MENU_ACTIVE_HOVER_BG = 'var(--ui-green-300)';
const MENU_HOVER_BG = 'rgba(45,66,106,0.06)';

// Block dnd-kit's pointer/mouse sensors from intercepting events inside the
// rename input so native text selection (drag-select, double-click words)
// works as expected.
const stopDragPropagation = {
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
};

export function DraggableTabLink({
  tab,
  variant,
  isActive,
  onNavigate,
  testId,
  tabRef,
  trailing,
  children,
  editing = false,
  editableValue,
  onRename,
}: DraggableTabLinkProps) {
  const { search } = useLocation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: editing });

  // Suppress hover-driven UI (highlight bg, 3-dot reveal via _groupHover)
  // while any tab in the bar/menu is being dragged.
  const isAnyDragActive = !!useDndContext().active;
  const justDraggedRef = useJustDraggedRef();

  const handleNavigate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (justDraggedRef.current === tab.id) {
      event.preventDefault();
      return;
    }
    onNavigate?.();
  };

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Source slot stays visible (dimmed) so the label is shown at the drop
    // position the dragged item will snap into.
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  };

  if (variant === 'bar') {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        display="inline-flex"
        alignItems="stretch"
      >
        <Link
          ref={(el: HTMLAnchorElement | null) => tabRef?.(el)}
          as={NavLink}
          to={{ pathname: tab.path, search }}
          onClick={handleNavigate}
          data-testid={testId}
          display="flex"
          alignItems="center"
          height="48px"
          my="-8px"
          px={`${TAB_HIT_PADDING_X}px`}
          fontSize="14px"
          fontWeight="600"
          color={isActive ? ACTIVE_TEXT_COLOR : BAR_TEXT_COLOR}
          whiteSpace="nowrap"
          _hover={{ color: ACTIVE_TEXT_COLOR, textDecoration: 'none' }}
        >
          {children ?? tab.label}
        </Link>
      </Box>
    );
  }

  // Hide the active bg while renaming so the browser's text-selection
  // highlight stays visible against the row.
  const showActiveBg = isActive && !editing;
  const dragProps = editing ? {} : { ...attributes, ...listeners };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...dragProps}
      role={isAnyDragActive ? undefined : 'group'}
      display="flex"
      alignItems="center"
      bg={showActiveBg ? MENU_ACTIVE_BG : 'transparent'}
      _hover={
        isAnyDragActive || editing
          ? undefined
          : { bg: showActiveBg ? MENU_ACTIVE_HOVER_BG : MENU_HOVER_BG }
      }
    >
      {editing ? (
        <Editable
          defaultValue={editableValue ?? tab.label}
          startWithEditView
          isPreviewFocusable={false}
          onSubmit={onRename}
          submitOnBlur
          flex="1"
          px="14px"
          py="5px"
          fontSize="14px"
          color="#2d426a"
          {...stopDragPropagation}
        >
          <EditablePreview width="100%" />
          <Input
            as={EditableInput}
            variant="flushed"
            size="sm"
            fontSize="14px"
          />
        </Editable>
      ) : (
        <Link
          as={NavLink}
          to={{ pathname: tab.path, search }}
          onClick={handleNavigate}
          data-testid={testId}
          display="flex"
          alignItems="center"
          flex="1"
          minWidth={0}
          px="14px"
          py="5px"
          fontSize="14px"
          fontWeight="400"
          fontFamily="Source Sans Pro, sans-serif"
          color="#2d426a"
          _hover={{ textDecoration: 'none' }}
        >
          <Box
            flex="1"
            minWidth={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {children ?? tab.label}
          </Box>
        </Link>
      )}
      {trailing}
    </Box>
  );
}
